/**
 * 多表格验证配置
 * 为新 ValidationEngine 提供验证规则
 */

import type { ValidationConfig } from '../../../core/dataProcessor/types';

// 🆕 为新 ValidationEngine 导出的配置
export const multiTableValidationConfig: ValidationConfig = {
  fields: [
    // 动态字段，由rows和columns定义
  ],
  businessRules: [
    {
      name: 'multi-table-configuration-valid',
      validator: (data, layer) => {
        const multiTableLayer = layer as any;

        // 🆕 树状表格例外：不需要预定义rows/columns配置
        if (multiTableLayer.isTreeTable) {
          return true;
        }

        const { encoding = {} } = multiTableLayer;
        const { rows = [], columns = [] } = encoding;

        // 普通表格至少需要rows或columns之一
        return rows.length > 0 || columns.length > 0;
      },
      message: 'Multi-table requires at least rows or columns configuration',
      suggestion:
        'Specify either rows array or columns array in the encoding, or set isTreeTable to true for tree tables',
      severity: 'error'
    },
    {
      name: 'multi-table-horizontal-columns-even',
      validator: (data, layer) => {
        const multiTableLayer = layer as any;
        const { encoding = {} } = multiTableLayer;
        const { rows = [], columns = [] } = encoding;

        // 横向一维表格需要偶数列
        if (rows.length && !columns.length && multiTableLayer.columnsNum) {
          return multiTableLayer.columnsNum % 2 === 0;
        }

        return true;
      },
      message: 'Horizontal table columnsNum must be even',
      suggestion: 'Set columnsNum to an even number for proper horizontal table layout',
      severity: 'error'
    },
    {
      name: 'multi-table-tree-structure-valid',
      validator: (data, layer) => {
        const multiTableLayer = layer as any;
        if (!multiTableLayer.isTreeTable) {
          return true;
        }

        // 树状表格数据结构验证
        return Array.isArray(data) && data.length > 0 && typeof data[0] === 'object';
      },
      message: 'Tree table requires valid hierarchical data structure',
      suggestion: 'Provide data as array of objects for tree table structure',
      severity: 'error'
    },
    {
      name: 'multi-table-sufficient-data',
      validator: data => Array.isArray(data) && data.length >= 1,
      message: 'Multi-table requires at least one data row',
      suggestion: 'Provide at least one data row for table display',
      severity: 'warning'
    },
    {
      name: 'multi-table-optimal-row-count',
      validator: data => Array.isArray(data) && data.length <= 1000,
      message: 'Multi-table works best with 1000 or fewer rows',
      suggestion: 'Consider pagination or data reduction for tables with many rows',
      severity: 'warning'
    },
    {
      name: 'multi-table-row-field-types',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return false;
        }
        const multiTableLayer = layer as any;
        const { encoding } = multiTableLayer;
        const rows = encoding?.rows || [];

        if (multiTableLayer.isTreeTable || rows.length === 0) {
          return true;
        }

        // 检查行字段是否为字符串或日期类型
        return rows.every((rowField: string) => {
          return data.every(item => {
            const value = item[rowField];
            // 简化为只检查字符串和数字类型，避免复杂的日期检查
            return typeof value === 'string' || typeof value === 'number';
          });
        });
      },
      message: 'Table row fields should be string or date types',
      suggestion: 'Ensure row fields contain string or date values for proper grouping',
      severity: 'warning'
    },
    {
      name: 'multi-table-column-field-types',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return false;
        }
        const multiTableLayer = layer as any;
        const { encoding } = multiTableLayer;
        const columns = encoding?.columns || [];

        if (multiTableLayer.isTreeTable || columns.length === 0) {
          return true;
        }

        // 检查列字段是否为字符串或数值类型
        return columns.every((columnField: string) => {
          return data.every(item => {
            const value = item[columnField];
            return typeof value === 'string' || typeof value === 'number';
          });
        });
      },
      message: 'Table column fields should be string or number types',
      suggestion: 'Ensure column fields contain string or numeric values for proper display',
      severity: 'warning'
    }
  ]
};
